import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaLink } from "@/components/landing/CtaLink";
import sorvaineLogo from "@/assets/sorvaine-logo-light.png";

const navLinks = [
  { label: "Product", href: "#product" },
  { label: "Pricing", href: "#pricing" },
  { label: "Platform", href: "/platform", isRoute: true },
  { label: "Blog", href: "/blog", isRoute: true },
];

export function BlogNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string, isRoute?: boolean) => {
    setIsMobileMenuOpen(false);
    if (isRoute) { navigate(href); return; }
    if (window.location.pathname !== '/') { navigate('/' + href); return; }
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "h-16 bg-white/90 backdrop-blur-xl border-b border-gray-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            : "h-20 bg-white"
        }`}
      >
        <div className="h-full max-w-[1200px] mx-auto px-6 flex items-center justify-between relative">
          <Link to="/" className="flex items-center">
            <img src={sorvaineLogo} alt="Sorvaine logo" className="h-[120px] w-auto" loading="eager" />
          </Link>

          <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href, link.isRoute)}
                className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors duration-300"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2">
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 px-5"
              >
                Log in
              </Button>
            </Link>
            <CtaLink to="/signup" cta="get_started" location="blog_nav">
              <Button className="rounded-md bg-primary hover:bg-primary/90 hover:-translate-y-[1px] px-6 h-9 !text-white font-semibold text-sm tracking-[0.01em] transition-all duration-200 shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                Get started
                <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
              </Button>
            </CtaLink>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden text-gray-500 hover:text-gray-900 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-50 bg-white md:hidden"
          >
            <div className="h-full flex flex-col">
              <div className="h-20 px-6 flex items-center justify-between border-b border-gray-100">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <img src={sorvaineLogo} alt="Sorvaine logo" className="h-[80px] w-auto" loading="lazy" />
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-400 hover:text-gray-900 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 px-6 py-8">
                {navLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href, link.isRoute)}
                    className="block w-full text-left text-base font-medium text-gray-700 py-4 border-b border-gray-100 hover:text-gray-900 transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
              <div className="p-6 space-y-3 border-t border-gray-100">
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button className="w-full rounded-md bg-primary hover:bg-primary/90 px-8 !text-white font-semibold text-sm">
                    Log in <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
